import JSZip from 'jszip';
import {
  buildRoasOverviewData,
  buildRoasOverviewParagraphs,
} from './buildRoasOverviewData';
import { fmtMoney } from './formatters';

const ODT_MIME = 'application/vnd.oasis.opendocument.text';

function escapeXml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function textParagraph(text, styleName) {
  return `<text:p text:style-name="${styleName}">${escapeXml(text)}</text:p>`;
}

function metricCell(primary, secondary) {
  return `<table:table-cell table:style-name="MetricCell">
      ${textParagraph(primary, 'MetricPrimary')}
      ${textParagraph(secondary, 'MetricSecondary')}
    </table:table-cell>`;
}

function metricsTableRow(cells) {
  return `<table:table-row>${cells
    .map(([primary, secondary]) => metricCell(primary, secondary))
    .join('')}</table:table-row>`;
}

export function buildRoasOverviewContentXml(data) {
  const reached100Label =
    data.totalCohorts > 0
      ? `${data.actualAtOrAbove100} / ${data.totalCohorts}`
      : '—';

  const metricsRows = [
    metricsTableRow([
      [String(data.totalCohorts), 'Cohorts underwritten'],
      [reached100Label, 'Reached 100% ROAS to date'],
      [fmtMoney(data.totalMarketingInvested), 'Marketing invested to date'],
    ]),
    metricsTableRow([
      [
        data.formatMonthLabel(data.medianBreakevenMonth),
        'Median months to breakeven, from spend',
      ],
      [
        data.formatPct(data.firstCashRoasPct),
        'Average first-cash ROAS (month 3)',
      ],
      [
        data.spendRampLabel,
        data.spendRampDates
          ? `Monthly spend ramp (${data.spendRampDates})`
          : 'Monthly spend ramp',
      ],
    ]),
  ].join('');

  const bodyParagraphs = buildRoasOverviewParagraphs(data)
    .map((text) => textParagraph(text, 'Body'))
    .join('');

  return `<?xml version="1.0" encoding="UTF-8"?>
<office:document-content xmlns:office="urn:oasis:names:tc:opendocument:xmlns:office:1.0"
  xmlns:text="urn:oasis:names:tc:opendocument:xmlns:text:1.0"
  xmlns:table="urn:oasis:names:tc:opendocument:xmlns:table:1.0"
  xmlns:style="urn:oasis:names:tc:opendocument:xmlns:style:1.0"
  xmlns:fo="urn:oasis:names:tc:opendocument:xmlns:xsl-fo-compatible:1.0"
  office:version="1.3">
  <office:automatic-styles>
    <style:style style:name="Title" style:family="paragraph">
      <style:text-properties fo:font-size="20pt" fo:font-weight="bold"/>
    </style:style>
    <style:style style:name="Subtitle" style:family="paragraph">
      <style:paragraph-properties fo:margin-bottom="0.18in"/>
      <style:text-properties fo:font-size="12pt"/>
    </style:style>
    <style:style style:name="Spacer" style:family="paragraph">
      <style:paragraph-properties fo:margin-bottom="0.12in"/>
    </style:style>
    <style:style style:name="Body" style:family="paragraph">
      <style:paragraph-properties fo:margin-bottom="0.08in"/>
      <style:text-properties fo:font-size="11pt"/>
    </style:style>
    <style:style style:name="MetricPrimary" style:family="paragraph">
      <style:text-properties fo:font-size="16pt" fo:font-weight="bold"/>
    </style:style>
    <style:style style:name="MetricSecondary" style:family="paragraph">
      <style:paragraph-properties fo:margin-bottom="0.08in"/>
      <style:text-properties fo:font-size="9pt" fo:color="#666666"/>
    </style:style>
    <style:style style:name="MetricsTable" style:family="table">
      <style:table-properties table:align="left" style:width="6.8in"/>
    </style:style>
    <style:style style:name="MetricCell" style:family="table-cell">
      <style:table-cell-properties fo:padding="0.08in" fo:border="0.5pt solid #dddddd"/>
    </style:style>
  </office:automatic-styles>
  <office:body>
    <office:text>
      ${textParagraph(`${data.companyName}  |  Diligence`, 'Title')}
      ${textParagraph('Initial review of Return on Ad Spend (ROAS)', 'Subtitle')}
      ${textParagraph('', 'Spacer')}
      <table:table table:style-name="MetricsTable">
        <table:table-column table:number-columns-repeated="3"/>
        ${metricsRows}
      </table:table>
      ${textParagraph('', 'Spacer')}
      ${bodyParagraphs}
    </office:text>
  </office:body>
</office:document-content>`;
}

const STYLES_XML = `<?xml version="1.0" encoding="UTF-8"?>
<office:document-styles xmlns:office="urn:oasis:names:tc:opendocument:xmlns:office:1.0"
  xmlns:style="urn:oasis:names:tc:opendocument:xmlns:style:1.0"
  xmlns:text="urn:oasis:names:tc:opendocument:xmlns:text:1.0"
  xmlns:fo="urn:oasis:names:tc:opendocument:xmlns:xsl-fo-compatible:1.0"
  office:version="1.3">
  <office:styles>
    <style:default-style style:family="paragraph">
      <style:text-properties style:font-name="Arial" fo:font-size="11pt"/>
    </style:default-style>
  </office:styles>
</office:document-styles>`;

const META_XML = `<?xml version="1.0" encoding="UTF-8"?>
<office:document-meta xmlns:office="urn:oasis:names:tc:opendocument:xmlns:office:1.0"
  xmlns:meta="urn:oasis:names:tc:opendocument:xmlns:meta:1.0"
  office:version="1.3">
  <office:meta>
    <meta:generator>Twelve Quick Cashflow</meta:generator>
  </office:meta>
</office:document-meta>`;

const MANIFEST_XML = `<?xml version="1.0" encoding="UTF-8"?>
<manifest:manifest xmlns:manifest="urn:oasis:names:tc:opendocument:xmlns:manifest:1.0">
  <manifest:file-entry manifest:media-type="${ODT_MIME}" manifest:full-path="/"/>
  <manifest:file-entry manifest:media-type="text/xml" manifest:full-path="content.xml"/>
  <manifest:file-entry manifest:media-type="text/xml" manifest:full-path="styles.xml"/>
  <manifest:file-entry manifest:media-type="text/xml" manifest:full-path="meta.xml"/>
</manifest:manifest>`;

export async function buildRoasOverviewOdtBlob({
  cohortSheet,
  companyName,
  filenameBase = 'quick-cashflow',
}) {
  const data = buildRoasOverviewData({ cohortSheet, companyName });
  if (!data) {
    return null;
  }

  const zip = new JSZip();
  zip.file('mimetype', ODT_MIME, { compression: 'STORE' });
  zip.file('content.xml', buildRoasOverviewContentXml(data));
  zip.file('styles.xml', STYLES_XML);
  zip.file('meta.xml', META_XML);
  zip.folder('META-INF')?.file('manifest.xml', MANIFEST_XML);

  const blob = await zip.generateAsync({
    type: 'blob',
    mimeType: ODT_MIME,
    compression: 'DEFLATE',
  });
  const safeBase = filenameBase.trim() || 'quick-cashflow';
  return {
    blob,
    filename: `${safeBase.replace(/[^\w.-]+/g, '-')}-ROAS-Overview.odt`,
  };
}
